// components/Header.tsx

"use client";
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { VerticalSeparator } from "@/components/ui/vertical-seperator";
import { useAuth } from "@/context/AuthContext";
import DashboardIcon from "@/assets/icons/dashboard-icon.svg";
import UserIcon from "@/assets/icons/user-icon.svg";
import { Menu, X } from "lucide-react";
import { ADMIN_URL } from "@/lib/config/urls";

const Header = () => {
  const [hasNotifications, setHasNotifications] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/marketplace", label: "Marketplace" },
    { href: "/consultation", label: "Consultation" },
    { href: "/financing", label: "Financing" },
    { href: "/recyclers", label: "Recyclers" },
    { href: "/blog", label: "Solar News" },
    { href: "/about", label: "About" },
  ];
  const path = usePathname();
  const { user } = useAuth();
  
  // Get role-specific profile link
  const getProfileLink = () => {
    if (!user) return "/auth/login";
    const role = user.role?.toUpperCase();
    if (role === "PROVIDER" || role === "DISTRIBUTOR") return "/dashboard/provider/profile";
    if (role === "RECYCLER") return "/dashboard/recycler/profile";
    if (role === "HOMEOWNER") return "/dashboard/user/profile";
    return "/dashboard";
  };

  // Check for notifications
  useEffect(() => {
    const checkNotifications = () => {
      const notifications = localStorage.getItem('pendingNotifications');
      setHasNotifications(notifications === 'true');
    };

    // Check on mount
    checkNotifications();

    // Listen for custom notification events
    window.addEventListener('newNotification', checkNotifications);
    window.addEventListener('clearNotifications', checkNotifications);

    return () => {
      window.removeEventListener('newNotification', checkNotifications);
      window.removeEventListener('clearNotifications', checkNotifications);
    };
  }, []);

  const getNavLinkClasses = (href: string) => {
    const isActive = href === path;
    let classes = "px-4 py-2 rounded-full transition-colors font-light text-gray-700 hover:text-green-600";
    if (isActive) {
      classes += " border-2 border-lightgreen text-lightgreen font-semibold";
    }
    return classes;
  };

  return (
    <header className="sticky top-0 z-50 bg-white text-gray-900 shadow-sm">
      <div className="container max-w-6xl mx-auto p-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center space-x-2">
          <Link href="/">
            <Image
              src="/logo.png"
              alt="Ren Power Group"
              width={160}
              height={60}
              className="w-auto h-10"
            />
          </Link>
        </div>

        <div className="flex items-center gap-3">
          {/* Desktop Navigation Links */}
          <nav className="text-sm hidden md:flex items-center space-x-2">
            {navLinks.map((nav) => (
              <Link
                href={nav.href}
                key={nav.label}
                className={getNavLinkClasses(nav.href)}
              >
                {nav.label}
              </Link>
            ))}
          </nav>

          <VerticalSeparator
            isLandingPage={false}
            className="hidden sm:flex"
          />

          {/* Action Buttons — visible on BOTH mobile and desktop */}
          {user ? (
            <div className="flex justify-between gap-4 items-center">
              <Link href={user.role === "ADMIN" ? ADMIN_URL : "/dashboard"} className="relative">
                <DashboardIcon className="w-5 h-5 text-black" />
                {hasNotifications && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-white animate-pulse"></span>
                )}
              </Link>
              <Link href={getProfileLink()}>
                <UserIcon className="w-5 h-5 text-black" />
              </Link>
            </div>
          ) : (
            <div className="flex gap-2 items-center">
              <Button
                asChild
                className="bg-darkgreen text-lightyellow hover:bg-darkgreen/90 hover:text-white rounded-full border-0 text-xs sm:text-sm px-3 sm:px-4"
              >
                <Link href="/auth/register">Get Started</Link>
              </Button>
              <Button
                asChild
                variant="ghost"
                className="text-gray-600 hidden md:flex"
              >
                <Link href="/auth/login">Log In</Link>
              </Button>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-md transition-colors text-gray-700 hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Off-Canvas Menu */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Off-canvas panel */}
        <div
          className={`absolute right-0 top-0 bottom-0 w-4/5 max-w-sm bg-white shadow-2xl transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
        
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <Image
              src="/logo.png"
              alt="Ren Power Group"
              width={160}
              height={60}
              className="w-auto h-10"
            />
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-md text-gray-700 hover:bg-gray-100"
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-col p-4 space-y-2">
            {navLinks.map((nav) => (
              <Link
                href={nav.href}
                key={nav.label}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors ${
                  nav.href === path ? "bg-green-50 text-green-700 font-semibold" : ""
                }`}
              >
                {nav.label}
              </Link>
            ))}
          </nav>

          {/* Divider */}
          <div className="mx-4 border-t border-gray-200" />

          {/* User Actions */}
          <div className="p-4 space-y-3">
            {user ? (
              <div className="space-y-3">
                <Link
                  href={user.role === "ADMIN" ? ADMIN_URL : "/dashboard"}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                >
                  <DashboardIcon className="w-5 h-5" />
                  <span className="font-medium">Dashboard</span>
                  {hasNotifications && (
                    <span className="ml-auto w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  )}
                </Link>
                <Link
                  href={getProfileLink()}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <UserIcon className="w-5 h-5" />
                  <span className="font-medium">Profile</span>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                <Button
                  asChild
                  className="w-full bg-lightgreen text-white hover:bg-green-700 rounded-full"
                >
                  <Link href="/auth/register" onClick={() => setIsMobileMenuOpen(false)}>
                    Get Started
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="w-full border-green-600 text-green-700 hover:bg-green-50 rounded-full"
                >
                  <Link href="/auth/login" onClick={() => setIsMobileMenuOpen(false)}>
                    Log In
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
